import React, {useEffect, useMemo} from 'react';
import {useAnalytics} from '../../hooks';
import DocumentHeader from '../../components/DocumentHeader';

import Link from '../../components/Link';
import Testimonial from '../../components/TestimonialCard';
import {Product} from '../../components/LinkedData';
import {getBrandName} from '../../components/Brand';

const MIN_FEATURED_LENGTH = 120;

const isStrongTestimonial = (t) => (
  Boolean(t?.title) && (t?.text?.trim().length ?? 0) >= MIN_FEATURED_LENGTH
);

// Stable partition: float "strong" testimonials (have a title and >=20 words)
// to the front while preserving original order within each group.
const sortTestimonials = (testimonials) => {
  const strong = [];
  const rest = [];
  testimonials.forEach((t) => {
    (isStrongTestimonial(t) ? strong : rest).push(t);
  });
  return [...strong, ...rest];
};

const CTACard = () => (
  <Link
    href="/upgrade?src=reviews"
    className="rounded-xl bg-gradient-to-r from-indigo-700 to-purple-700 shadow-xl"
  >
    <div className="flex flex-col justify-between h-full w-full py-4 px-6 sm:py-6 space-y-2 md:space-y-0">
      <div className="flex flex-col text-lg md:text-xl font-bold tracking-tight text-white leading-tight md:leading-tight">
        <span className="mb-1">{`Become an ${getBrandName()} member today`}</span>
        <span className="text-base font-normal opacity-75">{`Unlock everything ${getBrandName()} has to offer.`}</span>
      </div>
      <div
        href="/upgrade?src=reviews"
        className="whitespace-nowrap inline-flex w-full items-center justify-center rounded-md border border-transparent bg-white px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 md:w-auto"
      >
        Upgrade now →
      </div>
    </div>
  </Link>
);

export default function ReviewsPage({testimonials}) {
  const {tracker} = useAnalytics();

  const sortedTestimonials = useMemo(() => sortTestimonials(testimonials), [testimonials]);

  useEffect(() => {
    tracker.reviewsView();
  }, []);

  return (
    <div className="relative bg-gray-50 overflow-hidden">
      <Product
        name={`${getBrandName()} Membership`}
        aggregateRating={{
          ratingValue: 5,
          bestRating: 5,
          worstRating: 1,
          ratingCount: testimonials.length,
        }}
      />
      <DocumentHeader
        title={`${getBrandName()} Reviews`}
        socialDescription={`Read what hundreds of ${getBrandName()} customers had to say about our courses and mock interviews.`}
      />
      <div className="relative pt-6 pb-10 sm:pb-18">
        <main className="mx-auto max-w-5xl px-4 sm:mt-20">
          <div className="my-10">
            <h1 className="text-center text-3xl tracking-tight font-extrabold text-gray-900 sm:text-4xl md:text-5xl">
              {`${getBrandName()} Reviews`}
            </h1>
            <p className="mx-auto text-center mt-3 max-w-xl text-base font-regular text-gray-500 sm:text-lg md:mt-5 md:text-xl">
              Hear what hundreds of our community members have had to say about our
              courses and interview prep services after landing careers at top companies.
            </p>
          </div>
          <div className="flex flex-col">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Top review section */}
              {sortedTestimonials.slice(0, 9).map((testimonial) => (
                <Testimonial testimonial={testimonial} key={testimonial.id} />
              ))}

              <CTACard />

              {/* More reviews */}
              {sortedTestimonials.slice(9, 25).map((testimonial) => (
                <Testimonial testimonial={testimonial} key={testimonial.id} />
              ))}

              <CTACard />

              {/* Remainder of reviews */}
              {sortedTestimonials.slice(25).map((testimonial) => (
                <Testimonial testimonial={testimonial} key={testimonial.id} />
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
